<template>
  <el-form
    :style="activeFilter ? styleFilterEnabled : styleFilterDisabled"
    :label-position="labelPosition"
    :label-width="labelWidthFilter"
    :model="model"
    :rules="rules"
    @submit.native.prevent="doFilter"
    class="filter"
    ref="form"
  >
    <el-row :gutter="16" class="filter__container">
      <!-- First Name -->
      <el-col :lg="6" :md="16" :sm="24">
        <el-form-item
          :label="fields.firstName.label"
          :prop="fields.firstName.name"
        >
          <div class="form__item__label__slot" slot="label">
            <span>{{fields.firstName.label}}</span>
            <el-button v-if="model.firstName" type="text" @click="resetFieldFilter({ fieldKey: 'firstName'})"><app-i18n code="common.reset"/></el-button>
          </div>
          <el-input 
            v-model="model[fields.firstName.name]"
            :placeholder="fields.firstName.label"
            @input.native="debounceInput"
            @clear="doFilter"
            prefix-icon="icon-player"
          />
        </el-form-item>
      </el-col>

      <!-- Last Name -->
      <el-col :lg="6" :md="16" :sm="24">
        <el-form-item
          :label="fields.lastName.label"
          :prop="fields.lastName.name"
        >
          <div class="form__item__label__slot" slot="label">
            <span>{{fields.lastName.label}}</span>
            <el-button v-if="model.lastName" type="text" @click="resetFieldFilter({ fieldKey: 'lastName'})"><app-i18n code="common.reset"/></el-button>
          </div>
          <el-input 
            v-model="model[fields.lastName.name]"
            :placeholder="fields.lastName.label"
            @input.native="debounceInput"
            @clear="doFilter"
            prefix-icon="icon-player"
          />
        </el-form-item>
      </el-col>

      <!-- Gender -->
      <el-col :lg="5" :md="16" :sm="24">
        <el-form-item  :prop="fields.gender.name">
          <div class="form__item__label__slot" slot="label">
            <span>{{fields.gender.label}}</span>
            <el-button v-if="model.gender" type="text" @click="resetFieldFilter({ fieldKey: 'gender'})"><app-i18n code="common.reset"/></el-button>
          </div>
          <el-select 
            :placeholder="fields.gender.label" 
            v-model="model[fields.gender.name]"
            @change="doFilter"
          >
            <i
                  slot="prefix"
                  class="icon-family"
              />
            <el-option :value="undefined">--</el-option>
            <el-option
              :key="option.id"
              :label="option.label"
              :value="option.id"
              v-for="option in fields.gender.options"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-col>
      
      <!-- Birthdate Range -->
      <el-col :lg="6" :md="16" :sm="24">
        <el-form-item 
          :label="fields.birthdateRange.label" 
          :prop="fields.birthdateRange.name"
        >
          <div class="form__item__label__slot" slot="label">
            <span>{{fields.birthdateRange.label}}</span>
            <el-button v-if="enabledResetDateField" type="text" @click="resetFieldFilter({ fieldKey: 'birthdateRange'})"><app-i18n code="common.reset"/></el-button>
          </div>
          <!-- Watched -->
        <el-date-picker type="monthrange" format="MM/yyyy" v-model="model[fields.birthdateRange.name]"></el-date-picker>
        </el-form-item>
      </el-col> 

      <!-- Buttons -->
      <el-col :lg="1" :md="16" :sm="24">
        <div class="filter-buttons">
          <el-button
            :disabled="loading"
            @click="doResetFilter"
            icon="icon-undo"
            circle
          />
        </div>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { FilterSchema } from '@shared/form/filter-schema';
import { InscriptionModel } from '@modules/inscription/inscription-model';
import { i18n } from '@shared/i18n';
import _ from 'lodash';

export default {
  name: 'app-inscription-list-filter',

  props: {
    manage: {
      type: Object,
      required: true,
    },
    activeFilter: {
      type: Boolean,
      required: false
    }
  },

  data() {
    return {
      model: {},
      styleFilterEnabled: {
        display: 'initial'
      },
      styleFilterDisabled: {
        display: 'none'
      }
    };
  },

 
  async mounted() {
    this.model = this.filterSchema.initialValues(
      this.filter,
      this.$route.query,
    );

    this.doFilter()
  },

  computed: {
    ...mapGetters({
      labelPosition: 'layout/labelPosition',
      labelWidthFilter: 'layout/labelWidthFilter',
      loading: 'inscription/list/loading',
      filter: 'inscription/list/filter',
      activeSubtab: 'layout/headerTabs/activeSubtab',
    }),

    fields() {
      return InscriptionModel.fields({ propertyName: '' });
    },

    filterSchema() {
      return new FilterSchema([
        this.fields.id,
        this.fields.createdAtRange,
        this.fields.birthdateRange,
        this.fields.firstName,
        this.fields.lastName,
        this.fields.gender,
      ]);
    },

    rules() {
      const { filterSchema } = this;
      const rules = filterSchema.rules();
      return rules;
    },

    enabledResetDateField() {
      const isArray = Array.isArray(this.model.birthdateRange);
      const size = isArray ? this.model.birthdateRange.length : 0;
    
      return size > 1;
    }
  },

  methods: {
    ...mapActions({
      doReset: 'inscription/list/doReset',
      doFetch: 'inscription/list/doFetch',
    }),

    async doResetFilter() {
      this.model = this.filterSchema.initialValues();

      this.$refs.form.clearValidate();

      const status  = this.activeSubtab.type;
      const defaultFilter = status ? { status } : null;

      return this.doReset({ defaultFilter, manage: this.manage });
    },

    async resetFieldFilter({ fieldKey }) {
      if (this.model[`${fieldKey}`]) this.model[`${fieldKey}`] = null;
      this.doFilter();
    },

    debounceInput: _.debounce(function () {
      this.doFilter();
    }, 500),

    async syncInscription() {
      await this.validate()
    },

    async doFilter() {
      try {
        await this.$refs.form.validate();
        this.$refs.form.clearValidate();
      } catch (error) {
        return;
      }

      const filter = this.getFilterOptions();

      return this.doFetch({
        manage: this.manage,
        filter,
      });
    },

    getFilterOptions() {
      const filter = this.filterSchema.cast(this.model);

      if (this.activeSubtab) filter.status = this.activeSubtab.type;
      if (this.activeSubtab.type == 'all')  {
        filter.status = null
      }
      
      return filter;
    },

    i18n(code) {
      return i18n(code);
    },
  },

  watch: {
    activeSubtab: 'doFilter',
    'model.birthdateRange': 'doFilter'
  },
};
</script>

<style>
</style>
